import { useEffect, useRef, useState } from "react";
import "rsa/rsa.scss";

// Updated by hand (or by a deploy script) each time a new build is pushed to
// /var/www/avah_staging_html/downloads/ — see the deploy note in
// docs/domain_cutover_avahanservices.md. Not committed to the frontend
// bundle: the APK is scp'd to the server directly, not built by CRA.
const APK_URL = "/downloads/avahan-latest.apk";
// TODO: replace once the app is live on the App Store.
const IOS_APP_STORE_URL = "";
// Matches `scheme` in avah_mobile/app.json.
const APP_SCHEME_URL = "avahan://";
const OPEN_ATTEMPT_TIMEOUT_MS = 1500;

function detectPlatform() {
  const ua = navigator.userAgent || navigator.vendor || window.opera || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  return "desktop";
}

/**
 * Fallback landing page for https://avahanservices.com/app.
 *
 * Android App Links / iOS Universal Links intercept this URL at the OS level
 * and open the native app directly *when it's installed* — this page only
 * ever renders in the browser when it isn't, so its only job is getting the
 * user from "no app" to "app installed" for each platform.
 */
export default function AppDownload() {
  const [platform, setPlatform] = useState("desktop");
  const [attemptingOpen, setAttemptingOpen] = useState(false);
  const openedRef = useRef(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    // iOS blocks unsolicited custom-scheme redirects without a user gesture
    // (and always needs the App Store CTA regardless), so the retry below
    // only helps Android.
    if (p !== "android") return;

    setAttemptingOpen(true);

    // Android App Links (autoVerify) should already have intercepted this
    // URL before the page ever loaded, *if* the app is installed and
    // verification has propagated — this redirect is only a backup for the
    // window where it hasn't (fresh install, or the user turned off "open
    // supported links" for the app). Tab going background means it worked.
    const onVisibilityChange = () => {
      if (document.hidden) openedRef.current = true;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.location.href = APP_SCHEME_URL;

    const timer = setTimeout(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (!openedRef.current) setAttemptingOpen(false);
    }, OPEN_ATTEMPT_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return (
    <div className="avah-rsa">
      <div className="avah-rsa__wrap">
        <div className="avah-rsa__card" style={{ textAlign: "center" }}>
          <h1 style={{ marginTop: 0 }}>Get the Avahan app</h1>

          {platform === "android" && attemptingOpen && <p>Opening the app…</p>}

          {platform === "android" && !attemptingOpen && (
            <>
              <p>Download the app to continue.</p>
              <a className="avah-rsa__btn avah-rsa__btn--primary" href={APK_URL} download>
                Download APK
              </a>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 16 }}>
                This isn't from the Play Store, so Android will warn about installing from an
                unknown source the first time — tap "Settings" on that prompt, allow installs from
                this browser, then open the downloaded file.
              </p>
            </>
          )}

          {platform === "ios" && (
            <>
              <p>Get Avahan from the App Store.</p>
              {IOS_APP_STORE_URL ? (
                <a className="avah-rsa__btn avah-rsa__btn--primary" href={IOS_APP_STORE_URL}>
                  Open App Store
                </a>
              ) : (
                <p className="avah-rsa__err">Not yet available on iOS — check back soon.</p>
              )}
            </>
          )}

          {platform === "desktop" && (
            <p>Open this link on your phone to install the Avahan app.</p>
          )}
        </div>
      </div>
    </div>
  );
}
